/**
 * AppErrorBoundary - Root-level React error boundary.
 *
 * POSITION IN HIERARCHY (outermost → innermost):
 *   1. AppErrorBoundary  - root catch-all, non-retryable, full-page fallback   ← this file
 *   2. ErrorGuard["route"] - per-route, retryable, shell preserved
 *   3. ErrorGuard[name]    - per-editor-panel, retryable, scoped fallback
 *
 * BEHAVIOUR:
 *   - Renders a minimal full-screen fallback (never a blank page)
 *   - Logs the error and component stack via logger
 *   - Emits module:error on eventBus so the rest of the runtime can observe
 *   - Does NOT retry - root-level crashes indicate structural breakage
 *
 * DELIBERATE NON-FEATURES (do not add):
 *   - No "Try Again" button - root crashes are not recoverable client-side
 *   - No error details displayed to user - security and cleanliness
 *   - No CRDTs / operational transforms - see architecture-backlog.md
 */

import React from "react";
import { logger }   from "@/system/logger";
import { eventBus } from "@/runtime/eventBus";

interface State { hasError: boolean }

export class AppErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    logger.error("AppErrorBoundary: caught unhandled render error", {
      message:        error.message,
      stack:          error.stack,
      componentStack: info.componentStack,
    });
    eventBus.emit({
      type:     "module:error",
      moduleId: "app",
      error:    error.message,
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-screen">
          <p className="text-sm text-muted-foreground">
            Something went wrong. Please refresh.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}
